import React, { forwardRef, useEffect, useState, type InputHTMLAttributes } from "react";
import clsx from "clsx";
import { useIsDarkMode } from "@/utils/colorScheme";

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  id?: string;
  suffix?: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: "start" | "end";
  canClear?: boolean;
  showClearButton?: boolean;
  onClear?: () => void;
  optional?: boolean;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(function FormInput(
  {
    label,
    id,
    suffix,
    icon,
    iconPosition = "end",
    prefix,
    canClear = false,
    showClearButton = false,
    onClear,
    className = "",
    value,
    optional = false,
    disabled = false,
    ...props
  },
  ref
) {
  const isDarkMode = useIsDarkMode();
  const [nativeColorScheme, setNativeColorScheme] = useState<
    "light" | "dark" | undefined
  >(undefined);

  useEffect(() => {
    setNativeColorScheme(isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  const hasValue = value && String(value).length > 0;
  const shouldShowClearButton = canClear && showClearButton && hasValue && !disabled;
  // Keep start-position icon visible even when clear button shows; hide only if icon would be at end and conflict
  const shouldShowIcon =
    icon && (!shouldShowClearButton || iconPosition === "start");

  const handleClear = () => {
    if (onClear) {
      onClear();
    }
  };

  return (
    <div className="flex flex-col w-full">
      {label && (
        <label
          htmlFor={id}
          className={clsx(
            "text-xs uppercase tracking-widest flex items-center gap-1 font-mono",
            disabled
              ? "text-slate-400 dark:text-slate-500"
              : "text-slate-800 dark:text-slate-300",
          )}
        >
          <span className="flex-grow px-2">{label}</span>
          {optional && <span className="text-slate-500">(Optional)</span>}
        </label>
      )}

      <div className={clsx(label && "mt-2", "flex items-center")}>
        <div className="relative w-full">
          {prefix && (
            <div
              className={clsx(
                "absolute left-3 top-1/2 -translate-y-1/2",
                disabled ? "text-slate-300 dark:text-slate-600" : "text-slate-400",
              )}
            >
              {prefix}
            </div>
          )}
          <input
            ref={ref}
            id={id}
            value={value}
            disabled={disabled}
            className={clsx(
              "block w-full rounded-xl px-3 py-1.5 text-base h-10",
              "outline-none",
              "border",
              disabled
                ? [
                    "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400",
                    "placeholder:text-slate-400",
                    "dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-500",
                    "dark:placeholder:text-slate-600",
                  ]
                : [
                    "bg-white text-slate-900 dark:bg-slate-900/80 dark:text-slate-200",
                    "border-slate-200 dark:border-slate-600",
                    "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                    "focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-700 dark:focus:ring-indigo-400",
                  ],
              shouldShowIcon && iconPosition === "end" && "pr-10",
              shouldShowIcon && iconPosition === "start" && "pl-10",
              shouldShowClearButton && "pr-10",
              prefix && "pl-8",
              props.type === "time" &&
                "[&::-webkit-calendar-picker-indicator]:hidden",
              props.type === "search" &&
                canClear &&
                "[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden [&::-moz-search-clear-button]:hidden",
              className
            )}
            style={
              nativeColorScheme
                ? { colorScheme: nativeColorScheme }
                : undefined
            }
            {...props}
          />
          {shouldShowIcon && (
            <div
              className={clsx(
                "pointer-events-none absolute inset-y-0 flex items-center",
                disabled ? "text-slate-300 dark:text-slate-600" : "text-slate-500",
                iconPosition === "end" ? "right-0 pr-3" : "left-0 pl-3"
              )}
            >
              {icon}
            </div>
          )}
          {shouldShowClearButton && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear"
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              <svg
                className="size-4 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        {suffix && (
          <div
            className={clsx(
              "ml-2 flex-shrink-0",
              disabled
                ? "text-slate-400 dark:text-slate-600"
                : "text-slate-700 dark:text-slate-300",
            )}
          >
            {suffix}
          </div>
        )}
      </div>
    </div>
  );
});

export default FormInput;
